import React, { useState, useEffect } from 'react';
import { Link, graphql } from 'gatsby';
import useStoryblok from '~/utils/storyblok-service';
import SbEditable from 'storyblok-react';

import Layout from '~/components/Layout';
import Seo from '~/components/SEO';
import Image from '~/components/Dynamic/Image';
import DynamicComponent from '~/components/DynamicComponent';
import Button from '~/components/Dynamic/Button';
import RichTextRenderer from '~/components/Dynamic/RichTextRenderer';
import OldTextCard from '~/components/TextCard';
import TextCard from '~/components/Dynamic/TextCard';
import tick from '~/icons/tick.svg';
import hand from '~/icons/hand.svg';

const PricingPlan = (props) => {
  const { _uid, heading, content, button, price, price_subheading, features, pricingPlanPrice, pricingTable, suggested, suggestedDescription } = props;
  const [computedPrice, setComputedPrice] = useState(price);

  useEffect(() => {
    // Set dynamic price if exists
    if (pricingPlanPrice?.length) {
      setComputedPrice(pricingPlanPrice[0].price);
    }
  }, [pricingPlanPrice]);

  return (
    <SbEditable content={props} key={_uid}>
      <div className="flex flex-col flex-auto px-10 py-12 bg-beige">
        <h3 className="font-extrabold text-h3">{heading}</h3>
        <div className="my-3 text-15px" dangerouslySetInnerHTML={{ __html: content }} />
        <p className="mb-1 font-bold text-black uppercase text-13px mt-9 text-opacity-30">{suggested}</p>
        <p className="text-15px">{suggestedDescription}</p>

        {/* {price_subheading && <p className="uppercase text-sh mt-9">{price_subheading}</p>}
        {computedPrice && <h2 className="my-4 font-extrabold leading-none text-52px font-heading">{computedPrice}</h2>}
        {pricingPlanPrice?.length > 0 && (
          <div className="flex -ml-3 divide-x mt-9 text-14px">
            {pricingPlanPrice.map((plan) => (
              <div key={plan._uid} className="px-3">
                <button
                  onClick={() => setComputedPrice(plan.price)}
                  className={`outline-none ${computedPrice === plan.price && 'font-bold'}`}
                >
                  {plan.buttonText}
                </button>
              </div>
            ))}
          </div>
        )} */}

        <div className="icon-list">
          <RichTextRenderer content={features} className="font-semibold text-15px mt-9" />
        </div>

        {button.length > 0 && (
          <div className="mt-auto">
            <div className="mt-8">
              <Button blok={button[0]} />
            </div>
          </div>
        )}

      </div>
    </SbEditable>
  );
};

export default function PricingTemplate({ data, location, pageContext }) {
  const story = useStoryblok(data.storyblokEntry, location);
  const hero = story?.content?.hero[0];
  const offers = story?.content?.offers;
  const pricingTable = story?.content?.pricingTable[0]
  const components = story?.content?.content?.map((blok) => {
    return <DynamicComponent blok={blok} key={blok._uid} />;
  });

  return (
    <Layout locale={pageContext.locale}>
      <Seo {...story?.content?.seo} lang={story.lang} pageContext={pageContext} />

      {hero && (
        <section className="-mt-20 bg-white md:-mt-12">
          <div className="container">
            <div className="flex flex-col items-center py-24 -ml-6 md:py-40 md:flex-row">
              <div className="flex w-full pl-6 mt-6 md:w-7/12 xl:w-6/12 md:mt-0">
                <OldTextCard {...hero.textCard[0]} />
              </div>
              <div className="flex flex-col w-full pl-6 mt-16 md:mt-0 md:w-5/12 xl:w-6/12">
                <div className="md:max-w-[484px] mx-auto w-full">
                  <Image blok={hero.asset[0]} />
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center pb-32 -mt-16 lg:pb-40 ">
              {offers[0].content && (
                <h5 className="mb-8 font-extrabold text-center uppercase text-h5">{offers[0].content}</h5>
              )}
              <div className="grid grid-cols-2 md:grid-cols-4 w-full lg:max-w-[80%]">
                {offers.map((offer) => (
                  <>
                    {offer.component === 'textCard' && (
                      <div className="flex items-center justify-center mb-8 lg:mb-0">
                        <Image blok={offer.icon} width={56} className="w-auto mx-0"/>
                        <div className="ml-4 text-17px text-h4 md:text-20px">{offer.heading}</div>
                      </div>
                    )}
                  </>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      <section className="-mt-20">
        <div className="container">
          <div className="flex flex-wrap justify-center -ml-1 space-y-6 md:space-y-0 md:grid md:grid-cols-3 md:gap-3">
            {story.content.pricing_plans.map((plan) => (
              <div key={`plan${plan._uid}`} className="flex flex-col">
                <PricingPlan {...plan} />
              </div>
            ))}
          </div>

          {story.content?.pricingPlansButton?.length > 0 && (
            <div className="flex justify-center">
              <SbEditable content={story.content?.pricingPlansButton[0]}>
                <Link
                  to={story.content.pricingPlansButton[0].button.url}
                  className="flex justify-center cursor-pointer c-btn c-btn--outline c-btn--outline--black c-btn--longer"
                >
                  {story.content.pricingPlansButton[0].title}
                </Link>
              </SbEditable>
            </div>
          )}
        </div>
      </section>




      {pricingTable && (
        <section class="my-32">
          <div class="container">
            <div className="flex flex-col items-center justify-center mb-24 text-center">
              {pricingTable.intro[0] && (
                <TextCard blok={pricingTable.intro[0]} />
              )}
            </div>
            <div class="grid grid-cols-6 md:grid-cols-10 mb-4 md:mb-8 sticky z-10 top-[120px]">
              <div class="hidden md:block bg-black text-white col-span-4 text-center py-4 md:px-8 border-r-2 border-white">
                <h5 className="uppercase text-h4 text-15px">{pricingTable.feature}</h5>
              </div>
              <div class="bg-black text-white col-span-2 text-center py-4 px-2 md:px-8 border-r-2 border-white">
                <h5 className="uppercase text-h4 text-15px">{pricingTable.tier1}</h5>
              </div>
              <div class="bg-black text-white col-span-2 text-center py-4 px-2 md:px-8 border-r-2 border-white">
                <h5 className="uppercase text-h4 text-15px">{pricingTable.tier2}</h5>
              </div>
              <div class="bg-black text-white col-span-2 text-center py-4 px-2 md:px-8">
                <h5 className="uppercase text-h4 text-15px">{pricingTable.tier3}</h5>
              </div>
            </div>
            {pricingTable.groups.map((group, i) => (
              <div className="mb-8">
                {group.component === 'pricingTableGroup' ? (
                  <>
                  <div key={i} className="px-8 py-4 text-center uppercase bg-light-grey text-h4 text-15px md:text-left">{group.heading}</div>
                    {group.features.map((feature, i) => (
                      <div key={i} class="grid grid-cols-3 md:grid-cols-10 border-b border-light-grey py-6 pt-7 md:px-8 items-center">
                        <div className="col-span-3 mb-4 font-semibold md:col-span-4 md:mb-0">
                          <div className="relative flex justify-center mx-auto text-center select-none md:inline-block md:ml-0 group md:text-left">
                            <div className="flex items-start justify-center mb-4 md:justify-start md:mb-0">
                              <img src={hand} className="hidden w-6 h-auto mx-auto transform md:-ml-8 md:mr-2 -rotate-6 md:block" alt="" />
                              <div className="group-hover:underline">{feature.feature}</div>
                            </div>
                            {feature.featureHelper && (
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 md:translate-x-0 md:left-[110%] md:-top-3 text-center bg-black text-white p-4 py-6 w-64 opacity-0 transition-opacity duration-300 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto text-13px">
                                <div className="absolute ml-1 transform rotate-90 -translate-x-1/2 border left-1/2 md:translate-x-0 -top-3 md:top-4 md:-left-4 md:rotate-0" style={{borderColor: 'transparent black transparent transparent', borderWidth: '.5rem .75rem .5rem 0'}}></div>
                                {feature.featureHelper}
                              </div>
                            )}
                          </div>
                        </div>
                        <div class="col-span-1 md:col-span-2 flex h-full items-start mb-0 md:text-center justify-center text-15px px-2 text-center ">
                          {feature.tier1 === 'Y' ? (
                            <img src={tick} className="w-5 md:mx-auto" alt="" />
                          ) : (
                            feature.tier1
                          )}
                        </div>
                        <div class="col-span-1 md:col-span-2 flex h-full items-start mb-0 md:text-center justify-center text-15px px-2 text-center">
                          {feature.tier2 === 'Y' ? (
                            <img src={tick} className="w-5 md:mx-auto" alt="" />
                          ) : (
                            feature.tier2
                          )}
                        </div>
                        <div class="col-span-1 md:col-span-2 flex h-full items-start mb-0 md:text-center justify-center text-15px px-2 text-center">
                          {feature.tier3 === 'Y' ? (
                            <img src={tick} className="w-5 md:mx-auto" alt="" />
                          ) : (
                            feature.tier3
                          )}
                        </div>
                      </div>
                    ))}
                  </>
                ) : group.component === 'pricingTableGroupButtons' ? (
                  <div className="grid grid-cols-3 md:grid-cols-10">
                    <div className="flex items-center justify-start col-span-3 px-8 py-4 mb-4 text-center uppercase border-r-2 border-white md:col-span-4 text-h4 text-15px md:mb-0 md:text-left bg-light-grey">{group.heading}</div>
                    {group.buttons.map((button, i) => (
                      <div class="col-span-1 md:col-span-2 border-r-2 border-white">
                        <Button blok={button} className="justify-center" />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
        </div>
      </section>
    )}

    {components}
    </Layout>
  );
}

export const query = graphql`
  query PricingPageQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      full_slug
    }
  }
`;
